import { type AutocompleteProps } from '@mui/material'

import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import { SearchField } from 'components/common/SearchField'
import { VirtualizedListboxComponent } from 'components/common/VirtualizedListboxComponent'
import { getSelectedKubernetesTargets } from 'components/services/create/form-controls/fingerprint/infra/service-target-utils'
import { KubernetesAutocompleteSearchOption } from 'components/services/create/form-controls/fingerprint/kubernetes/KubernetesAutocompleteSearchOption'
import { type TargetingStrategy } from 'types/api/attacks/details/targeting'
import { type K8sObjectWithOwningTeamId } from 'types/api/kubernetes/kubernetes'

import type { K8sAutocompleteSearchOption } from 'components/services/create/form-controls/fingerprint/kubernetes/types'

export function getCommonK8sObjectsAutocompleteProps<TMultiple extends boolean>(
  hasSelectedOptions?: boolean
): Omit<
  AutocompleteProps<K8sAutocompleteSearchOption, TMultiple, false, false>,
  'options'
> & {
  ListboxProps: {
    OptionComponent: typeof KubernetesAutocompleteSearchOption
  }
} {
  return {
    clearOnBlur: false,
    disableListWrap: true,
    filterSelectedOptions: true,
    isOptionEqualToValue: (option, value) => option.uid === value.uid,
    ListboxComponent: VirtualizedListboxComponent,
    ListboxProps: {
      OptionComponent: KubernetesAutocompleteSearchOption
    },
    popupIcon: null,
    renderGroup: (params) => params,
    renderInput: (params) => (
      <SearchField
        {...params}
        {...(!hasSelectedOptions && {
          placeholder: 'Narrow the number of potential targets by name'
        })}
        label="Targets"
      />
    ),
    renderOption: (props, option) => [props, option],
    slotProps: { paper: { elevation: 8 } }
  }
}

/**
 * Returns a targetingStrategy for Kubernetes target types
 */
export function getKubernetesTargetingStrategy(
  k8sClientsFlat: K8sObjectWithOwningTeamId[],
  formValues: {
    selectedNamespace?: string
    selectedObjects: Set<string>
  },
  currentTeamId: string
): TargetingStrategy {
  return {
    type: ProvenanceTargetType.Kubernetes,
    names: getSelectedKubernetesTargets(k8sClientsFlat, formValues).map(
      ({ name, namespace, kind, clusterId, owningTeamId }) => ({
        name,
        namespace,
        kind,
        clusterId,
        // pass in owningTeamId only if it's a shared namespace
        ...(currentTeamId === owningTeamId ? {} : { owningTeamId })
      })
    )
  }
}
